import React, { useState, useEffect, useCallback } from 'react';
import { Activity, RotateCw, AlertCircle } from 'lucide-react';
import AllJobsTable from './AllJobsTable';
import CommercialAdmin from './CommercialAdmin';
import PricingConfigPanel from './PricingConfig';
import PromotionsAdmin from './PromotionsAdmin';
import AdminErrorLogs from './AdminErrorLogs';
import RateLimitsTab from './RateLimitsTab';
import CapAnalyticsTab from './CapAnalyticsTab';
import UtmDashboard from './UtmDashboard';
import { useAuth } from '../../contexts/AuthContext';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';
const REFRESH_INTERVAL = 5_000;

interface SystemStats {
  tpm: { used: number; limit: number; percent: number };
  flash3Tpm: { used: number; limit: number; percent: number };
  rpm: { used: number; limit: number; percent: number };
  rpd: { used: number; limit: number; percent: number };
  jobs: { pending: number; running: number; queued: number; doneToday: number; errorToday: number };
  perUser: Array<{
    userId: string;
    displayName: string;
    running: number;
    queued: number;
    tokensToday: number;
    rpmPct?: number;
    tpmPct?: number;
    rpdPct?: number;
  }>;
  recentErrors: Array<{
    id: string;
    jobId: string;
    userName: string;
    error: string;
    createdAt: string;
  }>;
  throughput?: number;
  avgJobTime?: number;
  stuckCount?: number;
  costToday?: number;
}

const AdminDashboard: React.FC = () => {
  const { session } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refiring, setRefiring] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'running' | 'error' | 'commercial' | 'pricing' | 'promotions' | 'errorlogs' | 'ratelimits' | 'capanalytics' | 'attribution'>('all');

  const fetchStats = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setError((data as any).error || 'Failed to fetch stats');
        return;
      }
      setStats(await res.json() as SystemStats);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleRefire = async () => {
    setRefiring(true);
    try {
      await fetch(`${WORKERS_URL}/api/dispatch-next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Internal-Key': session?.access_token || '' },
        body: JSON.stringify({ completedJobId: 'admin-manual' }),
      });
      setTimeout(fetchStats, 2000);
    } catch {}
    setRefiring(false);
  };

  const s = stats;
  const tpmPct = s?.tpm.percent ?? 0;
  const flash3Pct = s?.flash3Tpm?.percent ?? 0;
  const rpmPct = s?.rpm?.percent ?? 0;
  const rpdPct = s?.rpd?.percent ?? 0;
  const isErr = !!error || (s?.stuckCount ?? 0) > 0 || (s?.jobs.errorToday ?? 0) > 0;
  const throughput = s?.throughput ?? 0;
  const stuckCount = s?.stuckCount ?? 0;
  const costToday = s?.costToday ?? 0;

  return (
    <div className="h-full flex bg-slate-100 font-sans text-slate-800 overflow-hidden">
      
      {/* ========================================================= */}
      {/* LEFT COLUMN: INFO (1/3) - System Metrics & Overview       */}
      {/* ========================================================= */}
      <div className="w-1/3 min-w-[320px] max-w-md flex flex-col border-r border-slate-300 bg-slate-50 divide-y divide-slate-200 overflow-y-auto shadow-inner">
        
        {/* Section 1: Core System Pulse */}
        <div className="p-4 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isErr ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`}></div>
              <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Infrastructure</span>
            </div>
            <div className="flex items-center gap-2">
              {stuckCount > 0 && (
                <button
                  onClick={handleRefire}
                  disabled={refiring}
                  className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded text-[9px] font-bold uppercase transition-all flex items-center gap-1 shadow-sm active:scale-95"
                >
                  <RotateCw className={`w-2.5 h-2.5 ${refiring ? 'animate-spin' : ''}`} />
                  Fix {stuckCount} Stuck
                </button>
              )}
              <span className={`px-2 py-0.5 rounded text-[10px] font-black tracking-tighter ${isErr ? 'bg-red-600 text-white' : 'bg-emerald-500 text-white'}`}>
                {isErr ? 'ISSUE DETECTED' : s ? 'OPTIMAL' : 'CONNECTING'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MetricBox label="Pending" val={s?.jobs.pending ?? 0} />
            <MetricBox label="Queued" val={s?.jobs.queued ?? 0} color="text-amber-600" />
            <MetricBox label="Running" val={s?.jobs.running ?? 0} color="text-blue-600" />
            <MetricBox label="Done" val={s?.jobs.doneToday ?? 0} color="text-emerald-600" />
          </div>
          
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-[11px] font-mono mb-2">
              <span className="text-slate-400 uppercase text-[9px] font-sans font-bold">API Bandwidth (TPM)</span>
              <span className="font-bold">{tpmPct.toFixed(1)}%</span>
            </div>
            <div className="bg-slate-200 h-2 rounded-full overflow-hidden mb-1">
              <div 
                className={`h-full transition-all duration-1000 ease-out ${tpmPct > 80 ? 'bg-red-500' : 'bg-indigo-600'}`} 
                style={{ width: `${Math.min(tpmPct, 100)}%` }} 
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
              <span>{Math.round((s?.tpm.used ?? 0)/1000)}K used</span>
              <span>{Math.round((s?.tpm.limit ?? 0)/1000)}K cap</span>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] font-mono mb-2">
              <span className="text-slate-400 uppercase text-[9px] font-sans font-bold">Flash-3.0 TPM (audit)</span>
              <span className="font-bold">{flash3Pct.toFixed(1)}%</span>
            </div>
            <div className="bg-slate-200 h-2 rounded-full overflow-hidden mb-1">
              <div
                className={`h-full transition-all duration-1000 ease-out ${flash3Pct > 80 ? 'bg-red-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.min(flash3Pct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
              <span>{Math.round((s?.flash3Tpm?.used ?? 0)/1000)}K used</span>
              <span>{Math.round((s?.flash3Tpm?.limit ?? 0)/1000)}K cap</span>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] font-mono mb-2">
              <span className="text-slate-400 uppercase text-[9px] font-sans font-bold">Global RPM</span>
              <span className="font-bold">{rpmPct.toFixed(1)}%</span>
            </div>
            <div className="bg-slate-200 h-2 rounded-full overflow-hidden mb-1">
              <div
                className={`h-full transition-all duration-1000 ease-out ${rpmPct > 80 ? 'bg-red-500' : 'bg-emerald-600'}`}
                style={{ width: `${Math.min(rpmPct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
              <span>{s?.rpm?.used ?? 0} req/min</span>
              <span>{s?.rpm?.limit ?? 0} cap</span>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] font-mono mb-2">
              <span className="text-slate-400 uppercase text-[9px] font-sans font-bold">Global RPD</span>
              <span className="font-bold">{rpdPct.toFixed(1)}%</span>
            </div>
            <div className="bg-slate-200 h-2 rounded-full overflow-hidden mb-1">
              <div
                className={`h-full transition-all duration-1000 ease-out ${rpdPct > 80 ? 'bg-red-500' : 'bg-purple-600'}`}
                style={{ width: `${Math.min(rpdPct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
              <span>{(s?.rpd?.used ?? 0).toLocaleString()} req today</span>
              <span>{(s?.rpd?.limit ?? 0).toLocaleString()} cap</span>
            </div>
          </div>
        </div>

        {/* Section 2: Economics & Performance */}
        <div className="p-4 bg-white grid grid-cols-2 gap-4">
           <div className="flex flex-col">
              <span className="text-[9px] uppercase font-bold text-slate-400 mb-1">Today's Cost</span>
              <span className="text-xl font-mono font-bold text-violet-700 tracking-tighter">${costToday.toFixed(3)}</span>
           </div>
           <div className="flex flex-col border-l border-slate-100 pl-4">
              <span className="text-[9px] uppercase font-bold text-slate-400 mb-1">Velocity</span>
              <span className="text-xl font-mono font-bold text-slate-800 tracking-tighter">{throughput}<span className="text-[10px] text-slate-400 ml-1 font-normal italic">jobs/5m</span></span>
           </div>
        </div>

        {/* Section 3: User Resource Consumption */}
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50 p-4">
          <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-3 flex items-center gap-2">
            Top Consumers
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar font-mono text-[11px]">
            {s?.perUser.map((u: SystemStats['perUser'][number]) => {
              const rpm = u.rpmPct ?? 0;
              const tpm = u.tpmPct ?? 0;
              const rpd = u.rpdPct ?? 0;
              const barColor = (pct: number) => pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-emerald-500';
              return (
                <div key={u.userId} className="bg-white p-2 rounded border border-slate-200 group hover:border-indigo-300 transition-all shadow-sm hover:shadow-md">
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-700 truncate w-24 group-hover:text-indigo-600" title={u.displayName}>{u.displayName}</span>
                      <span className="text-violet-600 text-[10px]">${(u.tokensToday * 0.0000005).toFixed(4)}</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="text-right">
                        <div className="text-[9px] text-slate-400">RUN</div>
                        <div className="text-[11px] font-bold text-blue-600">{u.running}</div>
                      </div>
                      <div className="text-right border-l border-slate-100 pl-2">
                        <div className="text-[9px] text-slate-400">QUE</div>
                        <div className="text-[11px] font-bold text-amber-600">{u.queued}</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 mt-1">
                    <div title={`RPM: ${rpm}% of 120/min`}>
                      <div className="flex justify-between text-[8px] text-slate-400">
                        <span>RPM</span><span>{rpm}%</span>
                      </div>
                      <div className="bg-slate-200 h-1 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor(rpm)}`} style={{ width: `${Math.min(rpm, 100)}%` }} />
                      </div>
                    </div>
                    <div title={`TPM: ${tpm}% of 2M tokens/min`}>
                      <div className="flex justify-between text-[8px] text-slate-400">
                        <span>TPM</span><span>{tpm}%</span>
                      </div>
                      <div className="bg-slate-200 h-1 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor(tpm)}`} style={{ width: `${Math.min(tpm, 100)}%` }} />
                      </div>
                    </div>
                    <div title={`RPD: ${rpd}% of 5K/day`}>
                      <div className="flex justify-between text-[8px] text-slate-400">
                        <span>RPD</span><span>{rpd}%</span>
                      </div>
                      <div className="bg-slate-200 h-1 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor(rpd)}`} style={{ width: `${Math.min(rpd, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {!s?.perUser.length && <div className="text-slate-400 text-center py-8 italic">No active sessions</div>}
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* RIGHT COLUMN: MAIN DATA TABLE (2/3)                       */}
      {/* ========================================================= */}
      <div className="flex-1 flex flex-col bg-white min-w-0">
        <div className="flex gap-8 px-8 pt-6 border-b border-slate-200 shrink-0 bg-white">
          <TabButton id="all" label="All History" active={activeTab} onClick={setActiveTab} />
          <TabButton id="running" label="Queue & Active" active={activeTab} onClick={setActiveTab} />
          <TabButton id="error" label="Action Required" active={activeTab} onClick={setActiveTab} />
          <TabButton id="commercial" label="Commercial" active={activeTab} onClick={setActiveTab} />
          <TabButton id="pricing" label="💰 Pricing" active={activeTab} onClick={setActiveTab} />
          <TabButton id="promotions" label="🎁 Promotions" active={activeTab} onClick={setActiveTab} />
          <TabButton id="errorlogs" label="🚨 Errors" active={activeTab} onClick={setActiveTab} />
          <TabButton id="ratelimits" label="🛡️ Rate Limits" active={activeTab} onClick={setActiveTab} />
          <TabButton id="capanalytics" label="📊 Cap Analytics" active={activeTab} onClick={setActiveTab} />
          <TabButton id="attribution" label="🎯 Attribution (UTM)" active={activeTab} onClick={setActiveTab} />
        </div>

        <div className="flex-1 overflow-hidden relative">
          {activeTab === 'commercial' ? (
            <CommercialAdmin />
          ) : activeTab === 'pricing' ? (
            <PricingConfigPanel />
          ) : activeTab === 'promotions' ? (
            <PromotionsAdmin />
          ) : activeTab === 'errorlogs' ? (
            <AdminErrorLogs />
          ) : activeTab === 'ratelimits' ? (
            <RateLimitsTab />
          ) : activeTab === 'capanalytics' ? (
            <CapAnalyticsTab />
          ) : activeTab === 'attribution' ? (
            <div className="h-full overflow-y-auto p-6 bg-slate-50">
              <UtmDashboard token={session?.access_token ?? null} />
            </div>
          ) : (
            <AllJobsTable forcedFilter={activeTab as 'all' | 'running' | 'error'} />
          )}
        </div>
      </div>
      
    </div>
  );
};

function MetricBox({ label, val, color = 'text-slate-800' }: { label: string, val: number, color?: string }) {
  return (
    <div className="bg-white border border-slate-200 p-2.5 rounded-lg shadow-sm">
      <span className="text-[9px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">{label}</span>
      <span className={`text-xl font-mono font-black ${color} block leading-none`}>{val}</span>
    </div>
  );
}

function TabButton({ id, label, active, onClick }: { id: any, label: string, active: string, onClick: any }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      className={`pb-3 text-[11px] uppercase font-black tracking-widest transition-all relative
        ${isActive ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-500'}
      `}
    >
      {label}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
      )}
    </button>
  );
}

export default AdminDashboard;
